import {
  FormDataType,
  FormStackProps,
  useTheme,
} from '@tw/tw-component-library';

// Extend FormStackProps to include updateFormData in additionalProps
type ExtendedFormStackProps = FormStackProps & {
  additionalProps?: FormStackProps['additionalProps'] & {
    updateFormData?: (data: Record<string, unknown>) => void;
  };
};
import { useFormConfiguration } from '../../container/forms/hooks/core/useFormConfiguration';
import { useMemo, useCallback, useRef } from 'react';
import React from 'react';
import { FormSection } from '../../container/forms/components/FormSection';
import { createLogger } from '../../../utils/logger';
import { FormWrapper, FormWrapperRef } from './FormWrapper';
import { shouldBypassWorkflowBackend } from '../../../utils/workflowTestingBypass';
import { stripSchemaValidationForTesting } from '../../container/forms/utils/stripSchemaValidationForTesting';
import type { FormSchemaConfig } from '../../container/forms/types';

const logger = createLogger('FormStackComponent');

export const FormStackComponent = React.memo(() => {
  const { theme } = useTheme();
  const formWrapperRef = useRef<FormWrapperRef>(null);
  const { formConfig, formManager, currentSection, isFormReady } =
    useFormConfiguration();

  // Form configuration with memoization
  const bypassBackend = shouldBypassWorkflowBackend(
    formManager?.formState.workflowType
  );

  const enhancedFormConfig = useMemo((): FormStackProps => {
    if (!formConfig) {
      return {
        formSchema: { sections: [] },
        initialData: {},
        validateOnChange: !bypassBackend,
        validateOnBlur: !bypassBackend,
      };
    }

    const rawSections =
      typeof formConfig.formSchema === 'string'
        ? []
        : formConfig.formSchema.sections || [];

    const schemaForRender = bypassBackend
      ? stripSchemaValidationForTesting({
          sections: rawSections,
        } as FormSchemaConfig)
      : { sections: rawSections };

    return {
      ...formConfig,
      formSchema: {
        sections: schemaForRender?.sections || [],
      },
      initialData: formManager?.formState.data || {},
      additionalProps: {
        ...formConfig.additionalProps,
        preSelectedTags: {},
        updateFormData: formManager.updateFormData,
      },
      validateOnChange: !bypassBackend,
      validateOnBlur: !bypassBackend,
    } as ExtendedFormStackProps;
  }, [formConfig, formManager, bypassBackend]);

  const handleValidSubmit = useCallback(
    async (validFormData: FormDataType) => {
      logger.update('FormStack validation passed, processing submission');

      try {
        const result = await formManager.handleSubmit(
          currentSection,
          validFormData
        );

        if (!result.isValid) {
          logger.warning(
            'Manager validation failed after FormStack validation'
          );
          return;
        }

        // Show section completion modal instead of auto-advancing
        formWrapperRef.current?.showSectionCompletionModal(currentSection);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        logger.error('Form submission error', { error: errorMessage });
        formManager.setErrors({ _form: errorMessage });
      }
    },
    [formManager, currentSection]
  );

  // Handle form errors from FormStack validation
  const handleFormError = useCallback(
    (errors: Record<string, string>) => {
      logger.warning('Form validation errors received', { errors });
      formManager.setErrors(errors);
    },
    [formManager]
  );

  // Memoize form section props
  const formSectionProps = useMemo(
    () => ({
      sectionKey: currentSection,
      formConfig: enhancedFormConfig,
      onChange: formManager.updateFormData,
      onError: handleFormError,
      onSubmit: handleValidSubmit,
    }),
    [
      currentSection,
      enhancedFormConfig,
      formManager.updateFormData,
      handleFormError,
      handleValidSubmit,
    ]
  );

  // Show loading state until form is ready
  if (!isFormReady) {
    return (
      <FormWrapper
        ref={formWrapperRef}
        showProgress={true}
        showHeader={true}
        isSingleSection={false}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            fontSize: '16px',
            color: theme.colors.text.secondary,
          }}
        >
          Loading form...
        </div>
      </FormWrapper>
    );
  }

  // Render
  return (
    <FormWrapper
      ref={formWrapperRef}
      showProgress={true}
      showHeader={true}
      isSingleSection={false}
    >
      <FormSection {...formSectionProps} />
    </FormWrapper>
  );
});
